import { Component, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { AdmytService } from '../../../services/admyt.service'
import { Firestore, collection, query, where, getDocs } from '@angular/fire/firestore'
import { FirestoreStructure } from '@parkupp/core'

interface AdmytUser {
    id: string
    firstName: string
    lastName: string
    email: string
    status?: string
    createdAt?: string
    updatedAt?: string
    cars?: any[]
}

interface LinkedParkUppClient {
    uid: string
    firstName: string
    lastName: string
}

@Component({
    selector: 'app-admin-list-admyt-users',
    templateUrl: './admin-list-admyt-users.component.html',
    styleUrls: ['./admin-list-admyt-users.component.scss'],
})
export class AdminListAdmytUsersComponent implements OnInit {
    users: AdmytUser[] = []
    filteredUsers: AdmytUser[] = []
    loading = true
    error: string | null = null
    Math = Math

    // Pagination
    currentPage = 1
    itemsPerPage = 20
    totalItems = 0
    totalPages = 0

    // Filtering
    searchTerm = ''
    statusFilter = 'all'
    availableStatuses = ['all', 'active', 'inactive', 'pending']

    // Linked ParkUpp clients
    linkedClients: Map<string, LinkedParkUppClient> = new Map()

    constructor(
        private router: Router,
        private admytService: AdmytService,
        private firestore: Firestore
    ) {}

    async ngOnInit() {
        await this.loadUsers()
    }

    async loadUsers() {
        try {
            this.loading = true
            this.error = null

            const allUsers: AdmytUser[] = []
            const pageSize = 200
            let offset = 0
            let total = Infinity

            while (offset < total) {
                const usersResult = await this.admytService.getUsers(undefined, pageSize, offset)
                const payload = usersResult.data as any
                const page = (payload.data || []) as AdmytUser[]
                allUsers.push(...page)
                total = payload.total ?? page.length
                offset += pageSize
                if (page.length < pageSize) break
            }

            this.users = allUsers
            this.totalItems = this.users.length
            this.applyFilters()

            // Load linked ParkUpp clients
            await this.loadLinkedClients()

            this.loading = false
        } catch (error) {
            console.error('Failed to load users:', error)
            this.error = 'Failed to load users. Please try again.'
            this.loading = false
        }
    }

    async loadLinkedClients() {
        this.linkedClients.clear()
        
        // Get unique emails from users
        const emails = this.users.map(user => user.email).filter(email => !!email)
        if (emails.length === 0) return

        try {
            const adminClientsRef = collection(this.firestore, FirestoreStructure.ADMIN_CLIENTS)

            for (const email of emails) {
                try {
                    const q = query(adminClientsRef, where('email', '==', email))
                    const snapshot = await getDocs(q)

                    if (!snapshot.empty) {
                        const doc = snapshot.docs[0]
                        
                        // Get the client's name from the clients collection
                        const clientsRef = collection(this.firestore, FirestoreStructure.CLIENTS)
                        const clientDoc = await getDocs(query(clientsRef, where('__name__', '==', doc.id)))

                        let firstName = ''
                        let lastName = ''

                        if (!clientDoc.empty) {
                            const clientData = clientDoc.docs[0].data()
                            firstName = clientData['firstName'] || ''
                            lastName = clientData['lastName'] || ''
                        }

                        this.linkedClients.set(email, {
                            uid: doc.id,
                            firstName,
                            lastName,
                        })
                    }
                } catch (err) {
                    console.error(`Failed to look up user by email ${email}:`, err)
                }
            }
        } catch (error) {
            console.error('Failed to load linked ParkUpp clients:', error)
        }
    }

    getLinkedClient(email: string): LinkedParkUppClient | null {
        return this.linkedClients.get(email) || null
    }

    applyFilters() {
        let filtered = [...this.users]

        // Apply search filter
        if (this.searchTerm.trim()) {
            const searchLower = this.searchTerm.toLowerCase()
            filtered = filtered.filter(
                (user) =>
                    user.firstName?.toLowerCase().includes(searchLower) ||
                    user.lastName?.toLowerCase().includes(searchLower) ||
                    user.email?.toLowerCase().includes(searchLower) ||
                    user.id?.toLowerCase().includes(searchLower)
            )
        }

        // Apply status filter
        if (this.statusFilter !== 'all') {
            filtered = filtered.filter((user) => user.status === this.statusFilter)
        }

        this.filteredUsers = filtered
        this.totalItems = filtered.length
        this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage)

        // Reset to first page if current page is out of bounds
        if (this.currentPage > this.totalPages && this.totalPages > 0) {
            this.currentPage = 1
        }
    }

    onSearchChange() {
        this.currentPage = 1
        this.applyFilters()
    }

    onStatusFilterChange() {
        this.currentPage = 1
        this.applyFilters()
    }

    getPaginatedUsers(): AdmytUser[] {
        const startIndex = (this.currentPage - 1) * this.itemsPerPage
        const endIndex = startIndex + this.itemsPerPage
        return this.filteredUsers.slice(startIndex, endIndex)
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page
        }
    }

    getPaginationPages(): number[] {
        const pages: number[] = []
        const maxVisiblePages = 5
        const half = Math.floor(maxVisiblePages / 2)

        let start = Math.max(1, this.currentPage - half)
        let end = Math.min(this.totalPages, start + maxVisiblePages - 1)

        // Adjust start if we're near the end
        if (end - start < maxVisiblePages - 1) {
            start = Math.max(1, end - maxVisiblePages + 1)
        }

        for (let i = start; i <= end; i++) {
            pages.push(i)
        }

        return pages
    }

    getFullName(user: AdmytUser): string {
        return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User'
    }

    getCarCount(user: AdmytUser): number {
        return user.cars?.length || 0
    }

    async refreshData() {
        await this.loadUsers()
    }

    // Stats computed properties
    getActiveCount(): number {
        return this.users.filter(user => user.status === 'active').length
    }

    getTotalCars(): number {
        return this.users.reduce((total, user) => total + (user.cars?.length || 0), 0)
    }

    /**
     * Truncate any value (string or number) to specified length
     */
    truncate(value: any, maxLength: number): string {
        if (value === null || value === undefined) return ''
        const str = String(value)
        if (str.length <= maxLength) return str
        return str.substring(0, maxLength) + '...'
    }
}
